import React from 'react';
import {StyleSheet, View} from 'react-native';
import CCText from '@/Atoms/CCText';
import {FontSizes} from '@/Utils/Dimensions';
import {MaterialCommunityIcons} from '@expo/vector-icons';
import PlayerAvatar from '@/Screens/Leaderboard/components/PlayerAvatar';
import {lbColors, lbRadius} from '@/Screens/Leaderboard/leaderboardTheme';

// ── ArenaStickyRankRow ───────────────────────────────────────────────────────
// The floating "you are #137" bar, shown when the player's own row is not on
// the page they are currently browsing. Deliberately NOT the Leaderboard's
// StickyYourRankBar: that one is modelled on the daily-wins board (MeVM,
// "N more wins to climb into the top 20") and neither its data nor its copy
// transfers to an arena, where the unit is points and rank is continuous.
// It does reuse the same visual language (dark panel, gold rank, PlayerAvatar)
// so the two surfaces still read as one product.
//
// Points/rank are per-user and arrive on the arena_status_response socket
// payload, which is 1:1 — the standings broadcast is per-room and can never
// carry a per-caller row.

interface Props {
  rank: number;
  points: number;
  gamesPlayed?: number;
  total?: number;
  username?: string;
  rating?: number;
  onFire?: boolean;
  // Tapping jumps the pager to the page holding this rank.
  onPress?: () => void;
}

const ArenaStickyRankRow: React.FC<Props> = ({
  rank,
  points,
  gamesPlayed,
  total,
  username,
  rating,
  onFire,
  onPress,
}) => {
  const sub =
    total && total > 0 ? `of ${total} players` : `${gamesPlayed ?? 0} games`;

  return (
    <View style={styles.bar} data-testid="arena-sticky-rank-row">
      <CCText
        fontSize={FontSizes[16]}
        fontWeight="bold"
        color={lbColors.gold}
        style={styles.rank}>
        {`#${rank}`}
      </CCText>
      <PlayerAvatar
        initials="ME"
        color={lbColors.teal}
        size={34}
        ring={lbColors.gold}
      />
      <View style={styles.middle}>
        <View style={styles.nameRow}>
          <CCText
            fontSize={FontSizes[14]}
            fontWeight="bold"
            color="#FFFFFF"
            numberOfLines={1}
            style={{flexShrink: 1}}>
            {username || 'You'}
          </CCText>
          {Boolean(rating) && (
            <CCText fontSize={FontSizes[11]} color={lbColors.darkText}>
              {`(${rating})`}
            </CCText>
          )}
          {onFire && (
            <MaterialCommunityIcons name="fire" size={13} color="#FF6B00" />
          )}
        </View>
        <CCText fontSize={FontSizes[11]} color={lbColors.darkText}>
          {sub}
        </CCText>
      </View>
      <View style={styles.right}>
        <CCText fontSize={FontSizes[18]} fontWeight="bold" color="#FFFFFF">
          {points}
        </CCText>
        <CCText fontSize={FontSizes[10]} color={lbColors.darkText}>
          points
        </CCText>
      </View>
      {onPress && (
        <MaterialCommunityIcons
          name="crosshairs-gps"
          size={18}
          color={lbColors.darkText}
          onPress={onPress}
          // affordance for "take me to my row"
          style={styles.jump}
        />
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  bar: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    backgroundColor: lbColors.darkPanel,
    paddingHorizontal: 12,
    paddingVertical: 10,
    borderRadius: lbRadius.md,
    marginTop: 8,
  },
  rank: {minWidth: 34, textAlign: 'center'},
  middle: {flex: 1, minWidth: 0},
  nameRow: {flexDirection: 'row', alignItems: 'center', gap: 6},
  right: {alignItems: 'flex-end', minWidth: 54},
  jump: {paddingLeft: 2},
});

export default ArenaStickyRankRow;
